import React, { useCallback, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Chip, ChipStatus, FontAwesomeIcon } from '@appcharge/shared-ui';
import usePermissions from 'api/usePermissions';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Loader2 } from 'lucide-react';
import { permissionsUtil } from 'utils/permissionsUtil';

import { usePages } from '../../api/portal/usePagesApi';
import ActionCell from '../../components/DataTableComponent/components/ActionCell';
import DataTable from '../../components/DataTableComponent/DataTable';
import DialogModal from '../../components/DialogModal/DialogModal';
import {
  modalConfigs,
  PORTAL_MODAL_NAMES
} from '../../components/DialogModalManager/constants/modalConfigs';
import EmptyState from '../../components/EmptyState/EmptyState';
import PortalHeader from '../../components/PortalHeader/PortalHeader';
import RichText from '../../components/RichText/RichText';
import { EToasterStatus, showToast } from '../../utils/showToast';

import { EditBlocksModal } from './EditBlocks/EditBlocksDialog';
import { EPagesTemplateOptions } from './PagesForm/constants';

const dayjsUTC = dayjs.extend(utc);

export const PagesTable = () => {
  const navigate = useNavigate();
  const { getPages, deletePage } = usePages();
  const { isSuperAdmin } = usePermissions();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPageIdForBlockEdit, setSelectedPageIdForBlockEdit] =
    useState<string>('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState<null | string>(null);

  const isLoading = getPages.isLoading || !getPages.data;
  const hasPages = getPages.data?.pages && getPages.data.pages.length > 0;
  const isEmpty = getPages.data?.pages && getPages.data.pages.length === 0;

  const filteredPages = getPages.data?.pages?.filter(
    (page: { pageName: string }) =>
      page.pageName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDeletePage = useCallback(async () => {
    if (!selectedPageId) return;

    const hasSubPages = getPages.data?.pages?.some(
      (page: { isSubPage: boolean; parentPageId: string }) =>
        page.isSubPage === true && page.parentPageId === selectedPageId
    );

    if (hasSubPages) {
      showToast({
        message: 'Cannot delete page',
        status: EToasterStatus.ERROR,
        description:
          'This page has sub-pages. Please delete or reassign sub-pages before deleting this page.'
      });
      setIsDeleteDialogOpen(false);
      setSelectedPageId(null);
      return;
    }

    deletePage.mutate(selectedPageId, {
      onSuccess: () => {
        showToast({
          message: 'Page deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        getPages.refetch();
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete page',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
      },
      onSettled: () => {
        setIsDeleteDialogOpen(false);
        setSelectedPageId(null);
      }
    });
  }, [deletePage, getPages, selectedPageId]);

  const showDeletePageModal = useCallback((pageId: string) => {
    setSelectedPageId(pageId);
    setIsDeleteDialogOpen(true);
  }, []);

  const COLUMNS: any = useMemo(
    () => [
      // {
      //   accessorKey: 'checkbox',
      //   header: <Checkbox variant="outlined" />,
      //   cell: () => <Checkbox variant="outlined" />
      // },
      {
        accessorKey: 'pageName',
        header: 'Page Name',
        cell: ({ getValue }: any) => getValue()
      },
      {
        accessorKey: 'id',
        header: 'ID',
        cell: ({ row }: any) => (
          <div
            className="group flex items-center justify-between w-full"
            data-testid={`PagesTable-IdCell-${row.original.id}`}
          >
            <span className="truncate max-w-[120px]">{row.original.id}</span>
            <FontAwesomeIcon
              icon="fa-regular fa-copy"
              className="cursor-pointer text-base-mutedForeground hover:text-base-foreground opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`PagesTable-CopyId-${row.original.id}`}
              onClick={() => {
                navigator.clipboard.writeText(row.original.id);
                showToast({
                  message: 'Page ID copied to clipboard',
                  status: EToasterStatus.SUCCESS
                });
              }}
            />
          </div>
        )
      },
      {
        accessorKey: 'pageTemplate',
        header: 'Page Type',
        cell: ({ getValue }: any) => getValue()
      },
      {
        accessorKey: 'createdAt',
        header: 'Created Date',
        cell: ({ getValue }: any) =>
          dayjsUTC.utc(getValue()).format('MMM DD, YYYY')
      },
      {
        accessorKey: 'publishDate',
        header: 'Last published',
        cell: ({ getValue }: any) => {
          const value = getValue();
          if (!value) return '';
          const date = dayjsUTC.utc(value);
          return date.isValid() ? date.format('MMM DD, YYYY') : '';
        }
      },
      {
        accessorKey: 'state',
        header: 'Status',
        cell: ({ getValue }: any) => {
          const statusMap: Record<string, ChipStatus> = {
            visible: 'success',
            hidden: 'primary',
            scheduled: 'warning'
          };
          return (
            <Chip
              variant="dot"
              status={statusMap[getValue()]}
              className="capitalize"
              data-testid={`PagesTable-StatusChip-${getValue()}`}
            >
              {getValue()}
            </Chip>
          );
        }
      },
      {
        accessorKey: 'actions',
        header: null,
        maxSize: 60,
        minSize: 60,
        size: 60,
        cell: ({ row }: any) => (
          <div
            className="max-w-[60px] w-[60px]"
            data-testid={`PagesTable-ActionCell-${row.original.id}`}
          >
            <ActionCell
              rowData={row.original}
              actions={[
                {
                  label: 'Edit',
                  handler: () => {
                    navigate(`./form/${row.original.id}`);
                  },
                  testId: `PagesTable-Action-Edit-${row.original.id}`
                },
                {
                  label: 'Duplicate',
                  handler: () => {
                    navigate(`./dup/${row.original.id}`);
                  },
                  testId: `PagesTable-Action-Duplicate-${row.original.id}`
                },
                {
                  label: 'Delete',
                  handler: () => showDeletePageModal(row.original.id),
                  disabled:
                    row.original.pageTemplate === EPagesTemplateOptions.HOME ||
                    row.original.pageTemplate === EPagesTemplateOptions.STORE,
                  testId: `PagesTable-Action-Delete-${row.original.id}`
                },
                {
                  label: 'Edit blocks',
                  handler: () => {
                    setSelectedPageIdForBlockEdit(row.original.id);
                  },
                  hidden: !isSuperAdmin,
                  testId: `PagesTable-Action-EditBlocks-${row.original.id}`
                }
              ]}
            />
          </div>
        )
      }
    ],
    [isSuperAdmin, navigate, showDeletePageModal]
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div
          className="absolute top-1/2 left-1/2"
          data-testid="PagesTable-Loading"
        >
          <Loader2 className="h-16 w-16 animate-spin text-indigo-300" />
        </div>
      );
    }

    if (isEmpty) {
      return (
        <EmptyState
          dataTestId="PagesTable-EmptyState" // ✅ test id
          title="Create a portal page"
          description="Build your portal by adding pages to fit your needs"
          buttonText="Start now"
          className="mx-auto mt-36"
          onClick={() => navigate('./form')}
        />
      );
    }

    return (
      <div
        className="border border-base-sidebarBorder rounded-[20px] mx-7 p-9 mb-7"
        data-testid="PagesTable-ContentWrapper"
      >
        <div data-testid="PagesTable-RichText">
          <RichText
            title="Pages"
            description="Create, view, and manage pages in your portal"
            className="mb-5"
          />
        </div>
        <div
          className="border border-base-sidebarBorder rounded-lg"
          data-testid="PagesTable-DataTable"
        >
          <DataTable
            data={filteredPages}
            columns={COLUMNS}
            rowIdIdentifier="id"
            bodyDataTestId="PagesTable-Body"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="overflow-y-scroll" data-testid="PagesTable-Wrapper">
      {' '}
      { }
      <PortalHeader
        title={isEmpty ? 'Pages' : 'My Pages'}
        primaryButtonText="Create New page"
        onPrimaryButtonClick={() => navigate('./form')}
        isSearchVisible={hasPages}
        isCreateButton
        dataTestId="PagesTable-Header"
        onSearchChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearchQuery(e.target.value)
        }
      />
      <EditBlocksModal
        pageId={selectedPageIdForBlockEdit}
        onClose={() => {
          setSelectedPageIdForBlockEdit('');
        }}
      />
      {isDeleteDialogOpen && (
        <DialogModal
          isOpen={isDeleteDialogOpen}
          dataTestId="PagesTable-DeleteModal"
          onOpenChange={(open: boolean) => {
            if (!open) setIsDeleteDialogOpen(false);
          }}
          title={modalConfigs[PORTAL_MODAL_NAMES.DELETE_PAGE].title}
          description={modalConfigs[PORTAL_MODAL_NAMES.DELETE_PAGE].description}
          modalActions={{
            onConfirm: { label: 'Delete', action: handleDeletePage },
            onClose: {
              label: 'Cancel',
              action: () => setIsDeleteDialogOpen(false),
              variant: 'link'
            }
          }}
        />
      )}
      {renderContent()}
    </div>
  );
};
